import React, { useMemo } from "react"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppDispatch } from "../../../../store/storeHooks"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { Merchant } from "../merchantTypes"
import { fetchMerchantsForSupplier, MerchantsResponse, SupplierMerchantsQuery } from "../merchantsApi"
import { pushContext } from "../../../../context/contextSlice"
import { useLocation, useNavigate } from "react-router-dom"
import { MERCHANT_REPORTS_ROUTE, SUPPLIER_REPORTS_ROUTE } from "../../../../../constants/routes"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useSupplier } from "../../../supplierHooks"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { MerchantAccessDetails } from "../components/MerchantAccessDetails"

type MerchantAccessProps = Readonly<{}>

const selectSupplierMerchants = (data: MerchantsResponse) => data?.merchants || []

export function MerchantAccess({}: MerchantAccessProps) {
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const { supplierId } = useSupplier()
    const { l10n } = useAppLocalization()
    const dispatch = useAppDispatch()

    const merchantsResult = useApiQuery(fetchMerchantsForSupplier, {
        queryName: SupplierMerchantsQuery.MERCHANTS_FOR_SUPPLIER,
        dependencies: {
            supplierId,
        },
        select: selectSupplierMerchants,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(merchantsResult)

    const columns = useMemo<MRT_ColumnDef<Merchant>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-merchant-access-table-header-case-no"),
                accessorKey: "caseNumber",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-merchant-access-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-merchant-access-table-header-address"),
                accessorKey: "addressDisplayText",
            }),
            columnFactory.endColumn({
                id: "arrow-indicator",
                header: "",
                enableColumnActions: false,
                Cell: () => <FontAwesomeIcon icon={faChevronRight} fontSize="small" />, // Render arrow icon
            }),
        ],
        [l10n, columnFactory],
    )

    const handleMerchantClick = (row: MRT_Row<Merchant>) => {
        dispatch(
            pushContext({
                type: "merchant",
                merchant: row.original,
                returnUrl: pathname,
            }),
        )

        navigate(MERCHANT_REPORTS_ROUTE)
    }

    return (
        <>
            <LocalizedStrict id="supplier-merchant-access-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_MERCHANT_ACCESS}
                    title="Merchant access"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-nav-item-merchant-access">
                            <Breadcrumb>Merchant access</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                muiTableBodyRowProps={({ row }) => ({
                    onClick: () => handleMerchantClick(row),
                    sx: { cursor: "pointer" },
                })}
                enableRowActions={false}
                renderDetailPanel={({ row }) => {
                    return <MerchantAccessDetails id={row.original.id} />
                }}
            />
        </>
    )
}
